import { FC } from "react";
import { useResolveUploadedPath, useTrans } from "../../../hooks";
import { Company, CompanyDocument, GhtGroup } from "../../../apis";
import {
    Box,
    VStack,
    Stack,
    Badge,
    HStack,
    Text,
    Button,
    Divider,
} from "@chakra-ui/react";
import { AppIcon, AppRenderDetailItem } from "../../../components";
import { APPC, UTIL } from "../../../config";

export const CompanyDetailView: FC<{
    data: Company;
}> = ({ data }) => {
    const { t } = useTrans();

    const downloadAttachmentHandler = async (cd: CompanyDocument) => {
        const filePath = useResolveUploadedPath(
            APPC.BE.Upload.FILETYPE.FILETYPE_COMPANY_FILE,
            cd.attachmentName
        );
        UTIL.downloadFileHandler(filePath, cd.attachmentName);
    };

    return (
        <>
            <VStack gap={4} alignItems={"stretch"}>
                <Stack
                    w={"full"}
                    gap={8}
                    direction={{ base: "column", sm: "column", md: "row" }}
                >
                    <Box w={{ base: "full", sm: "full", md: "50%" }}>
                        <VStack justifyContent={"space-between"} gap={8}>
                            <AppRenderDetailItem
                                label={t("ent.Company:name.label")}
                                value={data.name}
                            />
                            <AppRenderDetailItem
                                label={t("ent.Company:category.label")}
                                value={
                                    typeof data?.category === "object" &&
                                    data?.category?.name
                                        ? data.category.name
                                        : ""
                                }
                            />
                            <AppRenderDetailItem
                                label={t("ent.Company:region.label")}
                                value={
                                    typeof data?.region === "object" &&
                                    data?.region?.name
                                        ? data.region.name
                                        : ""
                                }
                            />
                            <AppRenderDetailItem
                                label={t("ent.Company:department.label")}
                                value={
                                    typeof data?.department === "object" &&
                                    data?.department?.name
                                        ? data.department.name
                                        : ""
                                }
                            />
                        </VStack>
                    </Box>
                    <Box w={{ base: "full", sm: "full", md: "50%" }}>
                        <AppRenderDetailItem
                            renderType="IMAGE"
                            imageUrl={useResolveUploadedPath(
                                APPC.BE.Upload.FILETYPE.FILETYPE_COMPANY_POSTER,
                                data?.imageName,
                                undefined
                            )}
                        />
                    </Box>
                </Stack>
                <AppRenderDetailItem
                    label={t("ent.Company:description.label")}
                    value={data?.description}
                    noOfLines={3}
                />
                <Stack
                    gap={8}
                    direction={{ base: "column", sm: "column", md: "row" }}
                >
                    <AppRenderDetailItem
                        label={t("ent.Company:externalCode.label")}
                        value={data.externalCode}
                    />
                    <AppRenderDetailItem
                        label={t("ent.Company:siret.label")}
                        value={data.siret}
                    />
                    <AppRenderDetailItem
                        label={t("ent.Company:phone.label")}
                        value={data.phone}
                    />
                </Stack>
                <Stack
                    w={"full"}
                    gap={8}
                    direction={{ base: "column", sm: "column", md: "row" }}
                >
                    <Box w={{ base: "full", sm: "full", md: "50%" }}>
                        <AppRenderDetailItem
                            label={t("ent.Company:address.label")}
                            value={data?.address}
                            noOfLines={5}
                        />
                    </Box>
                    <Box w={{ base: "full", sm: "full", md: "50%" }}>
                        <VStack justifyContent={"space-between"} gap={8}>
                            <AppRenderDetailItem
                                label={t("ent.Company:zipCode.label")}
                                value={data.zipCode}
                            />
                            <AppRenderDetailItem
                                label={t("ent.Company:city.label")}
                                value={data.city}
                            />
                        </VStack>
                    </Box>
                </Stack>
                <Stack
                    gap={8}
                    direction={{ base: "column", sm: "column", md: "row" }}
                >
                    <AppRenderDetailItem
                        label={t("ent.Company:latitude.label")}
                        value={data.latitude}
                    />
                    <AppRenderDetailItem
                        label={t("ent.Company:longitude.label")}
                        value={data.longitude}
                    />
                </Stack>
                <Stack
                    gap={8}
                    direction={{ base: "column", sm: "column", md: "row" }}
                >
                    <AppRenderDetailItem
                        label={t("ent.GhtGroup:name.label")}
                        value={(data?.ghtGroup as GhtGroup)?.name}
                    />
                    <AppRenderDetailItem
                        label={t("ent.Company:finessCode.label")}
                        value={data?.finessCode}
                    />
                </Stack>
                <Stack
                    gap={8}
                    direction={{ base: "column", sm: "column", md: "row" }}
                >
                    <AppRenderDetailItem
                        label={t("ent.Company:isActive.label")}
                    >
                        <HStack>
                            <Badge variant={data?.isActive ? "green" : "red"}>
                                {t(
                                    `cmn:label.active.${data?.isActive ? data?.isActive : "null"}`
                                )}
                            </Badge>
                        </HStack>
                    </AppRenderDetailItem>
                </Stack>
                {data?.companyDocuments &&
                    data?.companyDocuments?.length > 0 && (
                        <>
                            <Divider
                                borderColor={"var(--chakra-colors-primaryT70)"}
                            />
                            <Text textStyle={"semiBoldLg"}>
                                {t(
                                    "padm.CompanyDetailView:section.label.documents"
                                )}
                            </Text>
                            <VStack>
                                {(
                                    data?.companyDocuments as CompanyDocument[]
                                )?.map((d, index) => {
                                    return (
                                        <AppRenderDetailItem key={d.id}>
                                            <HStack>
                                                <Text textStyle={"mediumMd"}>
                                                    {`(${index + 1})`}
                                                </Text>
                                                <Text textStyle={"mediumMd"}>
                                                    {d.name}
                                                </Text>
                                                <Button
                                                    variant={"primary"}
                                                    onClick={() => {
                                                        downloadAttachmentHandler(
                                                            d
                                                        );
                                                    }}
                                                    size={"xs"}
                                                >
                                                    <AppIcon
                                                        name="icl-cloud-download"
                                                        w="1rem"
                                                    />
                                                    <Text ml={2}>
                                                        {t(
                                                            "cmn:option.download"
                                                        )}
                                                    </Text>
                                                </Button>
                                            </HStack>
                                        </AppRenderDetailItem>
                                    );
                                })}
                            </VStack>
                        </>
                    )}
            </VStack>
        </>
    );
};
